<template>
    <TailwindLayout title="Enterprise Applications">
        <div class="space-y-6">
            <BridgeBackLink />
            <header>
                <h1 class="text-2xl font-bold tracking-tight text-rent-risk-blue-400">Enterprise Applications</h1>
                <p class="mt-1 text-sm text-gray-600">
                    Experian intake submissions. Click a row to review the full application and download the forwardable PDF.
                </p>
            </header>

            <div class="flex flex-wrap gap-2">
                <button
                    v-for="option in ['all', ...statuses]"
                    :key="option"
                    type="button"
                    @click="applyFilter(option)"
                    class="px-3 py-1.5 rounded-md text-sm border"
                    :class="(filters.status || 'all') === option ? 'bg-rent-risk-blue-400 text-white border-rent-risk-blue-400' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'"
                >
                    {{ label(option) }}
                </button>
            </div>

            <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 text-sm">
                        <thead class="bg-gray-50 text-left">
                            <tr>
                                <th class="px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">Legal entity</th>
                                <th class="px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">Business type</th>
                                <th class="px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">Contact</th>
                                <th class="px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">Submitted</th>
                                <th class="px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-100">
                            <tr
                                v-for="app in applications.data"
                                :key="app.id"
                                class="hover:bg-gray-50 cursor-pointer"
                                @click="open(app.id)"
                            >
                                <td class="px-4 py-3 font-medium text-gray-900">{{ app.legal_entity_name }}</td>
                                <td class="px-4 py-3 text-gray-700">{{ app.business_type }}</td>
                                <td class="px-4 py-3 text-gray-700">{{ app.contact }}</td>
                                <td class="px-4 py-3 text-gray-700 whitespace-nowrap">
                                    {{ app.submitted_at }}
                                    <span v-if="app.days_since !== null" class="text-gray-400">({{ app.days_since }}d)</span>
                                </td>
                                <td class="px-4 py-3">
                                    <span class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium" :class="badge(app.status)">{{ label(app.status) }}</span>
                                </td>
                            </tr>
                            <tr v-if="!applications.data.length">
                                <td colspan="5" class="px-4 py-6 text-center text-gray-500">No applications found.</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <Pagination :links="applications.links" :results="applications" :type="'applications'" class="px-4 py-3 border-t border-gray-200" />
            </div>
        </div>
    </TailwindLayout>
</template>

<script setup>
import { router } from '@inertiajs/vue3'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import Pagination from '@/Shared/Pagination.vue'
import BridgeBackLink from '@/Shared/BridgeBackLink.vue'

const props = defineProps({
    applications: { type: Object, required: true },
    filters: { type: Object, default: () => ({}) },
    statuses: { type: Array, default: () => [] },
})

const label = (s) => s === 'all' ? 'All' : (s.charAt(0).toUpperCase() + s.slice(1)).replace(/_/g, ' ')

const badge = (status) => ({
    submitted: 'bg-blue-100 text-blue-800',
    forwarded: 'bg-amber-100 text-amber-800',
    more_info: 'bg-purple-100 text-purple-800',
    approved: 'bg-green-100 text-green-800',
    declined: 'bg-red-100 text-red-800',
}[status] || 'bg-gray-100 text-gray-700')

const applyFilter = (option) => {
    router.get(route('bridge.enterprise-applications.index'), option === 'all' ? {} : { status: option }, {
        preserveScroll: true,
        preserveState: true,
        replace: true,
    })
}

const open = (id) => router.visit(route('bridge.enterprise-applications.show', id))
</script>
